/*
 * Smooth (eased) wheel scrolling, the Lenis feel: every wheel tick moves a
 * target position and the page glides to it, closing about 18% of the
 * remaining distance per 60fps frame (lerp 0.18, wheel multiplier 1).
 *
 * This theme scrolls .page-wrapper (html and body are overflow: hidden), not
 * the window, so Lenis itself would not drive it; this does the same job on
 * that element and nothing else.
 *
 * Left native on purpose: touch (the OS momentum is better than ours),
 * keyboard, scrollbar drags, find-in-page, pinch zoom, horizontal scrolling,
 * any nested scroller that can still scroll in the wheel's direction (the
 * carousels, the comparison table, textareas), and anything marked
 * data-ruck-native-scroll. Skipped entirely under prefers-reduced-motion and
 * in the theme editor.
 *
 * Anything else that moves the page (an anchor jump, scrollTo, the keyboard)
 * is detected on the next scroll event and adopted, so this never fights it.
 */
(function () {
  'use strict';

  var LERP = 0.18; // share of the remaining distance closed per 1/60 s
  var WHEEL_MULTIPLIER = 1;
  var STOP_DISTANCE = 0.4; // px; below this the glide snaps to the target

  if (window.ruckSmoothScroll) return;
  if (window.Shopify && window.Shopify.designMode) return;
  if (window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  var scroller = document.querySelector('.page-wrapper');
  if (!scroller) return;

  var current = scroller.scrollTop; // where the glide is now
  var target = current; // where it is heading
  var lastWritten = current; // last scrollTop this script produced
  var frame = 0;
  var lastTime = 0;

  function maxScroll() {
    return Math.max(0, scroller.scrollHeight - scroller.clientHeight);
  }

  function clamp(value) {
    return Math.min(Math.max(value, 0), maxScroll());
  }

  // True when an element between the wheel's target and the page scroller can
  // still scroll vertically in the wheel's direction, so it should keep it.
  function nestedScrollerWantsWheel(node, deltaY) {
    for (var el = node; el && el !== scroller && el.nodeType === 1; el = el.parentElement) {
      if (el.hasAttribute('data-ruck-native-scroll')) return true;
      if (el.tagName === 'TEXTAREA' || el.tagName === 'SELECT') return true;
      var overflowY = window.getComputedStyle(el).overflowY;
      if ((overflowY === 'auto' || overflowY === 'scroll') && el.scrollHeight > el.clientHeight + 1) {
        var atTop = el.scrollTop <= 0;
        var atBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 1;
        if ((deltaY < 0 && !atTop) || (deltaY > 0 && !atBottom)) return true;
      }
    }
    return false;
  }

  function step(time) {
    var dt = Math.min(time - lastTime, 64); // a stalled tab must not leap
    lastTime = time;

    var remaining = target - current;
    if (Math.abs(remaining) <= STOP_DISTANCE) {
      current = target;
    } else {
      // Frame-rate independent: the same glide on 60, 120 and 144 Hz screens.
      current += remaining * (1 - Math.pow(1 - LERP, dt / (1000 / 60)));
    }

    scroller.scrollTop = current;
    lastWritten = scroller.scrollTop;

    if (current !== target) {
      frame = window.requestAnimationFrame(step);
    } else {
      frame = 0;
    }
  }

  function start() {
    if (frame) return;
    lastTime = window.performance.now();
    frame = window.requestAnimationFrame(step);
  }

  function onWheel(event) {
    if (event.defaultPrevented || event.ctrlKey || event.metaKey) return; // pinch zoom, browser zoom
    if (!event.deltaY || Math.abs(event.deltaX) > Math.abs(event.deltaY)) return; // horizontal
    if (nestedScrollerWantsWheel(event.target, event.deltaY)) return;
    if (maxScroll() <= 0) return;

    var delta = event.deltaY;
    if (event.deltaMode === 1) delta *= 40; // lines
    else if (event.deltaMode === 2) delta *= scroller.clientHeight; // pages

    // Only take over while the page can actually move that way; at an end the
    // event falls through untouched.
    var base = frame ? target : scroller.scrollTop;
    var next = clamp(base + delta * WHEEL_MULTIPLIER);
    if (next === base && !frame) return;

    event.preventDefault();
    if (!frame) current = scroller.scrollTop;
    target = next;
    start();
  }

  // Adopt a scroll this script did not make (anchor jump, scrollTo, keyboard,
  // scrollbar drag): stop gliding and follow it.
  function onScroll() {
    if (Math.abs(scroller.scrollTop - lastWritten) <= 1) return;
    if (frame) {
      window.cancelAnimationFrame(frame);
      frame = 0;
    }
    current = scroller.scrollTop;
    target = current;
    lastWritten = current;
  }

  // Native smooth scrolling (the theme sets scroll-behavior: smooth) would
  // chase every frame's write and fight the glide.
  scroller.style.scrollBehavior = 'auto';

  scroller.addEventListener('wheel', onWheel, { passive: false });
  scroller.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', function () {
    target = clamp(target);
  });

  window.ruckSmoothScroll = {
    destroy: function () {
      scroller.removeEventListener('wheel', onWheel);
      scroller.removeEventListener('scroll', onScroll);
      if (frame) window.cancelAnimationFrame(frame);
      scroller.style.scrollBehavior = '';
      delete window.ruckSmoothScroll;
    }
  };
})();
